import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Grid, Segment } from 'semantic-ui-react';
import { Header, ToastContext } from '@weezevent/nacre';
import { Trans, useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';

import { RowManager } from './rowManager';
import { RequestError } from '../../utils/errors';

export default function PartnerForm({ organization, event, history, reload = () => {} }) {
    const toast = React.useContext(ToastContext);

    const { t } = useTranslation();
    const [form, setForm] = React.useState([]);
    const [hasAccess, setHasAccess] = React.useState(false);
    const [inventories, setInventories] = React.useState([]);
    const [namePartner, setNamePartner] = React.useState('');
    const [emailPartner, setEmailPartner] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        fetcher.get(`/ticket/organizations/${organization.id}/events/${event.id}/partners/enabled`).then(response => {
            if (!response.response.enabled) {
                toast.error(t(`ticket.partner.form.access_denied`));
                return history.replace(`/ticket/O${organization.id}/E${event.id}/dashboard`);
            }
            setHasAccess(true);
        });
    }, []);

    React.useEffect(() => {
        if (!hasAccess) {
            return;
        }

        if (event) {
            let inventories = fetcher.get(`/ticket/organizations/${organization.id}/events/${event.id}/partners/availability/inventories`);
            inventories
                .then(invt => {
                    const mapToSelect = ({ uuid, name }) => ({ key: uuid, text: name, value: uuid });
                    let tmpInventories = invt.response.map(mapToSelect);
                    setInventories(tmpInventories);
                })
                .catch(e => {
                    toast.error(t(`ticket.partner.form.inventory_error`));
                });
        }
    }, [hasAccess]);

    const clickPlus = () => {
        if (form.length >= 50) {
            return;
        }

        if (form.length > 0) {
            let newRow = form[0];
            let newForm = [...form];
            newForm.push({
                seat: null,
                row: null,
                access: null,
                zone: null,
                inventory: newRow.inventory,
                event: newRow.event,
                name: null
            });
            setForm(newForm);
        }

        if (form.length === 0) {
            let newForm = [];
            newForm.push({
                seat: null,
                row: null,
                access: null,
                zone: null,
                inventory: null,
                event: null,
                name: null
            });
            setForm(newForm);
        }
    };

    const clickMinus = () => {
        if (form.length > 1) {
            let newForm = [...form];
            newForm.pop();
            setForm(newForm);
        }
    };

    const renderHeader = () => {
        return (
            <Grid.Row columns={6} stretched>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.inventory`)}</label>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.zone`)}</label>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.access`)}</label>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.row`)}</label>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.seat`)}</label>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.partner.form.name_form`)}</label>
                </Grid.Column>
            </Grid.Row>
        );
    };

    const emailIsValid = email => {
        return /\S+@\S+\.\S+/.test(email);
    };

    const handleSubmit = React.useCallback(() => {
        setLoading(true);
        let seats = [];
        let dataUncomplete = false;

        for (let row of form) {
            if (!row.seat || !row.inventory) {
                dataUncomplete = true;
            }
            seats.push({
                seat: row.seat,
                inventory: row.inventory,
                name: row.name || ''
            });
        }

        // Data validation
        if (dataUncomplete || !namePartner || !emailPartner) {
            setLoading(false);
            if (!emailIsValid(emailPartner)) {
                toast.error(t(`ticket.partner.form.email_invalid`));
                return;
            }
            toast.error(t(`ticket.partner.form.error_incomplete_data`));
            return;
        }

        fetcher
            .post(`/ticket/organizations/${organization.id}/events/${event.id}/partners/orders`, {
                data: {
                    partner_name: namePartner,
                    partner_email: emailPartner,
                    seats: seats
                }
            })
            .then(e => {
                toast.success(t(`ticket.partner.form.success`));
                return reload();
            })
            .catch(e => {
                let message = e.message;
                if (e instanceof RequestError) {
                    message = JSON.parse(e.response);
                    message = message.seats && message.seats.length ? message.seats[0].non_field_errors : message;
                }
                toast.error(`${t('ticket.partner.form.error')} ${message}`);
            })
            .finally(() => setLoading(false));
    });

    return (
        <div>
            <Segment>
                <Header title={<Trans i18nKey="ticket.partner.form.title">Partner Form</Trans>} />
                <Form>
                    <Grid>
                        <Grid.Row columns={2}>
                            {/* NAME PARTNER*/}
                            <Grid.Column>
                                <Form.Field>
                                    <label>{t(`ticket.partner.form.name`)}</label>
                                    <Form.Input style={{ height: '30px', width: '40%' }} value={namePartner} onChange={event => setNamePartner(event.target.value)} />
                                </Form.Field>
                            </Grid.Column>

                            {/* EMAIL PARTNER*/}
                            <Grid.Column>
                                <Form.Field>
                                    <label>{t(`ticket.partner.form.email`)}</label>
                                    <Form.Input style={{ height: '30px', width: '40%' }} value={emailPartner} onChange={event => setEmailPartner(event.target.value)} />
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Form>
            </Segment>

            <Grid className="overview-container">
                {form.length ? renderHeader() : null}

                {form.length
                    ? form.map((element, index) => (
                          <RowManager
                              key={index}
                              organizationId={organization.id}
                              eventId={event.id}
                              setForm={setForm}
                              form={form}
                              id={index}
                              inventories={inventories}
                              row={element}
                          />
                      ))
                    : null}
            </Grid>

            <div style={{ paddingTop: '20px' }}>
                <Button circular inverted color="red" floated={'right'} icon={'minus circle'} onClick={clickMinus} disabled={form.length > 1 ? false : true} />
                <Button circular primary floated={'right'} icon={'plus circle'} onClick={clickPlus} disabled={form.length >= 50} />
                <Button type="button" onClick={() => history.replace(`/ticket/O${organization.id}/E${event.id}/dashboard`)}>
                    <Trans i18nKey="ticket.common.action.cancel">Cancel</Trans>
                </Button>

                <Button primary loading={loading} onClick={handleSubmit} type="submit" disabled={form.length === 0 || loading}>
                    <Trans i18nKey="ticket.common.action.edit">Save</Trans>
                </Button>
            </div>
        </div>
    );
}

PartnerForm.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    reload: PropTypes.func.isRequired
};
